// src/services/BesoinsContext.js
import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import { fetchWithAuth } from "./AuthService";
import { useAuth } from "./AuthContext";
import { encryptWithUserKey, decryptWithUserKey } from "./EncryptNotes";

const BesoinsContext = createContext();

export const BesoinsProvider = ({ children }) => {
  const { userKey, setUserKey } = useAuth();
  const [besoins, setBesoins] = useState([]);
  const [loading, setLoading] = useState(false);
  const loadedRef = useRef(false);

  const fetchUserKey = useCallback(async () => {
    const res = await fetchWithAuth('/api/notes/userkey');
    if (!res.ok) throw new Error('Impossible de récupérer la userKey');
    const data = await res.json();
    setUserKey(data.userKey);
    return data.userKey;
  }, [setUserKey]);

  const getKey = useCallback(async () => {
    if (!userKey) return await fetchUserKey();
    return userKey;
  }, [userKey, fetchUserKey]);

  // --- Chargement complet depuis le backend ---
  const loadBesoins = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth("/api/besoins/all");
      if (!res.ok) throw new Error("Erreur lors du chargement des besoins");
      const data = await res.json();
      
      let key = null;
      if (data.besoins && data.besoins.length > 0) {
          key = await getKey();
          if (!key) throw new Error("Impossible de récupérer la clé utilisateur pour déchiffrer");
      }

      let decryptedBesoins = [];
      if (data.besoins && Array.isArray(data.besoins)) {
        decryptedBesoins = await Promise.all(
          data.besoins.map(async (besoin) => {
            try {
              const text = await decryptWithUserKey(besoin.text, key);
              const keywords = await Promise.all(
                (besoin.keywords || []).map(kw => decryptWithUserKey(kw, key))
              );
              return { ...besoin, text, keywords };
            } catch (err) {
              console.error("Erreur de décryptage pour le besoin :", besoin._id, err);
              return besoin;
            }
          })
        );
      }

      setBesoins(decryptedBesoins);
      loadedRef.current = true;
    } catch (err) {
      console.error("Erreur de chargement des besoins :", err);
    } finally {
      setLoading(false);
    }
  }, [getKey]); // Added getKey to dependencies of useCallback

  // --- Charge seulement si pas déjà chargé (évite les appels redondants) ---
  const checkAndLoadBesoins = useCallback(async () => {
    if (!loadedRef.current) {
      await loadBesoins();
    }
  }, [loadBesoins]);

  // --- Force un rechargement (ex: après une action externe) ---
  const refreshBesoins = useCallback(async () => {
    await loadBesoins();
  }, [loadBesoins]);

  // --- Création ---
  const addBesoin = async (besoin) => {
    const key = await getKey();
    
    const encryptedText = await encryptWithUserKey(besoin.text || "", key);
    const encryptedKeywords = await Promise.all(
      (besoin.keywords || []).map(kw => encryptWithUserKey(kw, key))
    );

    const payload = {
      ...besoin,
      text: encryptedText,
      keywords: encryptedKeywords,
    };

    const res = await fetchWithAuth("/api/besoins", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(errData.message || "Erreur lors de la création du besoin");
    }

    const data = await res.json();
    
    // On garde la version non-cryptée pour l'affichage state local
    const besoinForContext = {
      ...data.besoin,
      text: besoin.text,
      keywords: besoin.keywords
    };

    setBesoins((prev) => [besoinForContext, ...prev]);
    return besoinForContext;
  };

  // --- Mise à jour (partielle, comme le backend le permet) ---
  const updateBesoin = async (besoin) => {
    const { _id, ...updates } = besoin;
    
    const key = await getKey();

    if (updates.text !== undefined) {
      updates.text = await encryptWithUserKey(updates.text, key);
    }
    
    if (updates.keywords !== undefined) {
      updates.keywords = await Promise.all(
        updates.keywords.map(kw => encryptWithUserKey(kw, key))
      );
    }

    const res = await fetchWithAuth(`/api/besoins/${_id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updates),
    });

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(errData.message || "Erreur lors de la mise à jour du besoin");
    }

    const data = await res.json();
    
    const besoinForContext = {
      ...data.besoin,
      text: besoin.text,
      keywords: besoin.keywords
    };

    setBesoins((prev) => prev.map((b) => (b._id === _id ? besoinForContext : b)));
    return besoinForContext;
  };

  // --- Suppression locale (l'appel DELETE est fait par le composant appelant,
  //     comme pour handleDeleteNote dans Note.js, puis on retire du state ici) ---
  const deleteBesoin = (id) => {
    setBesoins((prev) => prev.filter((b) => b._id !== id));
  };

  const getBesoinsByScanId = (scanId) => {
    if (!scanId) return [];
    return besoins.filter(besoin => besoin.scanResultId === scanId);
  };

  return (
    <BesoinsContext.Provider
      value={{
        besoins,
        loading,
        checkAndLoadBesoins,
        refreshBesoins,
        addBesoin,
        updateBesoin,
        deleteBesoin,
        getBesoinsByScanId,
      }}
    >
      {children}
    </BesoinsContext.Provider>
  );
};

export const useBesoins = () => useContext(BesoinsContext);
